import React, { useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Fab from '@mui/material/Fab'
import Grid from '@mui/material/Grid'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import SaveIcon from '@mui/icons-material/Save'

import { ThemeContext, UserContext } from 'components/common/App'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'

// Mirror of the backend SHOP_MAX_BUYABLE (#277).
const MAX_BUYABLE = 4

/**
 * Shop Window curation (#277). Pick up to MAX_BUYABLE buyable mascots + one
 * "coming soon" teaser; this is the only place a mascot becomes buyable in the
 * student store. Loads the full (active, arted) catalog + the current window,
 * then saves the selection via ``admin.mascots.shop.set`` (which replaces the
 * whole window atomically). Rotation is manual — there is no schedule here.
 */
export default function MascotShopPage() {
  const theme = useContext(ThemeContext)
  // Curating the shop window is superuser-only (#290).
  const currentUser = useContext(UserContext)
  const isSuperuser = currentUser && currentUser.role === "superuser"

  const [mascots, setMascots] = useState(null)
  const [buyable, setBuyable] = useState([]) // ordered slugs
  const [teaser, setTeaser] = useState(null) // slug | null
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    theme.changeTheme("light-bg")
    window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    Promise.all([
      rpc.post("", buildRequest("admin.mascots.list", { page: 1, per_page: 200, search: "" })),
      rpc.post("", buildRequest("admin.mascots.shop.get", {})),
    ]).then(([listRes, shopRes]) => {
      const list = listRes.data.result
      const shop = shopRes.data.result
      if (list.message || shop.message) {
        if (list.code === 5 || shop.code === 5) signoutAndRedirectToIndex()
        setError(list.message || shop.message)
        return
      }
      // Only active, arted mascots can be put in the window (matches the backend
      // guard) — hide the rest so a save can't fail on an unavailable slug.
      setMascots(list.data.filter((m) => m.is_active && m.expressions && m.expressions.idle))
      setBuyable(shop.buyable || [])
      setTeaser(shop.coming_soon || null)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleBuyable = (slug) => {
    setError(null)
    setBuyable((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug)
      if (prev.length >= MAX_BUYABLE) return prev // over cap — button is disabled too
      return [...prev, slug]
    })
    setTeaser((prev) => (prev === slug ? null : prev)) // can't be both
  }

  const toggleTeaser = (slug) => {
    setError(null)
    setTeaser((prev) => (prev === slug ? null : slug))
    setBuyable((prev) => prev.filter((s) => s !== slug)) // can't be both
  }

  const save = () => {
    setSaving(true)
    setError(null)
    rpc.post(
      "",
      buildRequest("admin.mascots.shop.set", { buyable, coming_soon: teaser })
    ).then((response) => {
      const result = response.data.result
      if (result.message) {
        if (result.code === 5) signoutAndRedirectToIndex()
        setError(result.message)
      } else {
        setBuyable(result.buyable || [])
        setTeaser(result.coming_soon || null)
        setSaved(true)
      }
    }).finally(() => setSaving(false))
  }

  if (mascots === null) {
    return (
      <Container fixed sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>Shop Window</title>
      </Helmet>

      {isSuperuser && (
        <Fab
          color="primary"
          aria-label="save"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          disabled={saving}
          onClick={save}
        >
          <SaveIcon />
        </Fab>
      )}

      <Container fixed sx={{ pb: 10 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Shop Window
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Pick up to {MAX_BUYABLE} buyable mascots and one “coming soon” teaser.
          This is what students see in the store — only these mascots are buyable.
          Rotation is manual: change it whenever you like (the team aims for a
          biweekly refresh).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip color="primary" label={`Buyable: ${buyable.length} / ${MAX_BUYABLE}`} />
          <Chip variant="outlined" label={teaser ? `Teaser: ${teaser}` : 'Teaser: none'} />
        </Stack>

        <Grid container spacing={2}>
          {mascots.map((mascot) => {
            const buyIndex = buyable.indexOf(mascot.slug)
            const isBuyable = buyIndex !== -1
            const isTeaser = teaser === mascot.slug
            const addDisabled = !isBuyable && buyable.length >= MAX_BUYABLE
            return (
              <Grid item xs={6} sm={4} md={3} key={mascot.slug}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderWidth: isBuyable || isTeaser ? 2 : 1,
                    borderColor: isBuyable
                      ? 'primary.main'
                      : isTeaser
                        ? 'secondary.main'
                        : 'divider',
                  }}
                >
                  <Box
                    sx={{
                      height: 88,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={mascot.expressions.idle}
                      alt={mascot.name}
                      style={{ maxHeight: 80, maxWidth: '100%' }}
                    />
                  </Box>
                  <Typography variant="subtitle2" noWrap>
                    {mascot.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    ★ {mascot.cost}
                  </Typography>
                  <Stack spacing={0.5}>
                    <Button
                      size="small"
                      variant={isBuyable ? 'contained' : 'outlined'}
                      disabled={addDisabled}
                      onClick={() => toggleBuyable(mascot.slug)}
                    >
                      {isBuyable ? `Buyable #${buyIndex + 1}` : 'Add to shop'}
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      variant={isTeaser ? 'contained' : 'text'}
                      onClick={() => toggleTeaser(mascot.slug)}
                    >
                      {isTeaser ? 'Teaser ✓' : 'Set as teaser'}
                    </Button>
                  </Stack>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Container>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        message="Shop window saved"
      />
    </>
  )
}
